// Finance — FY26 levy (fund 2101-65-6560) budgetary position.
// Board-facing cash watch: budget control by object (are we tracking against
// spend — over/under encumbered?), funds-available trend, and open-PO
// encumbrance with carryover clearly flagged. Cardinal rule: Funds Available is
// the appropriation side only; revenue is never summed in.

import { useMemo, useState } from "react";
import {
  ComposedChart,
  Line,
  BarChart,
  Bar,
  Legend,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import {
  useFinanceSummary,
  useFinanceByObject,
  useFinanceOpenPos,
  useFinanceTrend,
  useObjectWatch,
  useLevyKpis,
  useFinanceAvailable,
} from "../hooks/useFinance";
import type { ObjectWatchRow } from "../hooks/useFinance";
import KPICard from "../components/dashboard/KPICard";
import ChartCard from "../components/dashboard/ChartCard";
import FundBadge from "../components/finance/FundBadge";
import FreshnessStrip from "../components/finance/FreshnessStrip";
import StaleDataBanner from "../components/finance/StaleDataBanner";
import FundsAvailableReconciler from "../components/finance/FundsAvailableReconciler";
import ExportCsvButton from "../components/finance/ExportCsvButton";
import InfoTip from "../components/shared/InfoTip";
import PageIntro, { type VerdictTone } from "../components/shared/PageIntro";
import PageShell from "../components/shared/PageShell";
import PageHeader from "../components/shared/PageHeader";
import {
  fiscalCloseDeadlines,
  fyContext,
  fyContextFromAsOf,
} from "../lib/fiscal";
import PriorYearBanner from "../components/finance/PriorYearBanner";
import type { CsvColumn } from "../lib/csv";
import {
  asNum,
  usd0,
  usdC,
  TOOLTIP_PROPS,
  ACCENT,
  MUTED,
  GRID,
  COLORS,
} from "../components/finance/charts";
import {
  TH_LG as TH,
  THR_LG as THR,
  PILL_ACTIVE,
  availableAccent,
  FetchErrorBanner,
} from "../components/finance/ui";

const shortObj = (s: string) => (s.length > 24 ? s.slice(0, 23) + "…" : s);

export default function Finance() {
  const {
    data: summary,
    isLoading: sumLoading,
    error: summaryError,
  } = useFinanceSummary();
  const { data: kpis, isLoading: kpiLoading, error: kpisError } = useLevyKpis();
  const { data: availPair, isLoading: availPairLoading } =
    useFinanceAvailable();
  const { data: trend, isLoading: trendLoading } = useFinanceTrend();
  const { data: byObject, isLoading: objLoading } =
    useFinanceByObject("expenditure");
  const { data: openPos, isLoading: poLoading } = useFinanceOpenPos();

  // FY transition context. `fyc.currentFy` = the FY we're IN (clock); `fyc.dataFy`
  // = the FY the loaded levy numbers are FOR (their as-of date). When they differ
  // (it's FY27 but the county hasn't loaded FY27 yet, so we hold FY26),
  // `fyc.isPriorYear` is true and the page frames itself as prior-year data, not
  // today's position. `fyLabel` labels the data blocks with their actual FY.
  // Prefer the worker's EXPLICIT fiscal_year (the resolved fund/fy slice from
  // the 0094 data layer) over deriving from as_of — at the FY boundary the
  // FY26 CLOSE position carries a July as-of date (pulled 7/5), which the
  // as-of derivation would misread as current-FY data.
  const fyAsOf = summary?.as_of_date ?? kpis?.as_of_date ?? null;
  const explicitFy = summary?.fiscal_year ?? kpis?.fiscal_year ?? null;
  const fyc = explicitFy ? fyContext(explicitFy) : fyContextFromAsOf(fyAsOf);
  const fyLabel = fyc.dataFy ?? fyc.currentFy;
  const { data: watch, isLoading: watchLoading } = useObjectWatch();

  const [watchScope, setWatchScope] = useState<"watch" | "all">("watch");
  const [poScope, setPoScope] = useState<"all" | "carryover">("all");
  // Budget-control detail (Discretionary Available / Budget Consumed / Objects
  // Over Budget) collapses behind a disclosure so the top of the page reads as a
  // clean 4-KPI headline for non-finance board members.
  const [showBudgetDetail, setShowBudgetDetail] = useState(false);

  // Headline Funds Available (verified snapshot from /api/finance/levy-kpis) —
  // used by the verdict banner. The full 4-KPI headline row (Budget / Spent /
  // Encumbrance / Funds Available) is owned by the Overview page; this page leads
  // with the budgetary-control DETAIL instead, so it isn't an Overview clone.
  const remainingBalance = asNum(kpis?.remaining_balance);

  const available = asNum(summary?.funds_available);
  const encumbered = asNum(summary?.open_pos?.encumbered);
  const poCount = asNum(summary?.open_pos?.count);
  const cfCount = asNum(summary?.open_pos?.carryforward_count);
  const expBudget = asNum(summary?.expenditure?.budget);
  const expConsumed = asNum(summary?.expenditure?.consumed);
  const consumedPct =
    expBudget > 0 ? Math.round((expConsumed / expBudget) * 100) : 0;

  const trendData = (trend?.data ?? []).map((r) => ({
    date: String(r.as_of_date).slice(5),
    value: asNum(r.funds_available),
  }));
  const objRows = byObject?.data ?? [];
  const poRows = openPos?.data ?? [];

  // CSV export for the by-object budgetary table — the exact rows shown.
  const objExportColumns: CsvColumn<(typeof objRows)[number]>[] = [
    { key: "object_segment", header: "Object" },
    { key: "object_desc", header: "Description" },
    {
      key: "budget_amount",
      header: "Budget",
      format: (v) => Math.round(asNum(v)),
    },
    {
      key: "commitment_amount",
      header: "Committed",
      format: (v) => Math.round(asNum(v)),
    },
    {
      key: "obligation_amount",
      header: "Obligated",
      format: (v) => Math.round(asNum(v)),
    },
    {
      key: "actual_amount",
      header: "Actual",
      format: (v) => Math.round(asNum(v)),
    },
    {
      // NOT "Available" — this per-object figure is gross budget remaining
      // (Budget − Committed − Obligated − Actual). The spendable Funds Available
      // (budgetary control) is far lower ($24,913). Labeling it "Available" led
      // finance staff to download a file overstating what we can spend.
      key: "funds_available",
      header: "Budget Remaining (gross)",
      format: (v) => Math.round(asNum(v)),
    },
  ];
  const watchRows = useMemo<ObjectWatchRow[]>(() => watch?.data ?? [], [watch]);

  const overCount = watchRows.filter((r) => r.flag === "over").length;
  const insights = watchRows.filter((r) => r.flag === "over").slice(0, 4);

  // Budget control chart: grouped Budget vs (Spend + Encumbrance stacked) per object.
  const chartRows = useMemo(() => {
    const base =
      watchScope === "watch"
        ? watchRows.filter((r) => r.flag !== "ok" || r.open_encumbrance > 0)
        : watchRows;
    return base.slice(0, 14).map((r) => ({
      name: shortObj(String(r.object_desc || r.object_segment)),
      seg: r.object_segment,
      Budget: Math.round(r.budget),
      Spend: Math.round(r.actual),
      Encumbrance: Math.round(r.open_encumbrance),
      pct: r.pct_projected,
    }));
  }, [watchRows, watchScope]);

  const filteredPos =
    poScope === "carryover"
      ? poRows.filter((r) => asNum(r.carryover) === 1)
      : poRows;
  const carryoverRem = poRows
    .filter((r) => asNum(r.carryover) === 1)
    .reduce((s, r) => s + asNum(r.remaining), 0);

  // ── Verdict banner — derived from live data, never hardcoded. The headline
  // "where are we" sentence: Funds Available + days remaining to fiscal close +
  // what Finance is doing about it. Tone follows the same thresholds as the
  // Remaining Balance card.
  const { daysToClose } = fiscalCloseDeadlines();
  const verdictReady = !kpiLoading && kpis != null;
  // Prior-year mode: that fiscal year is closed, so no live "tight / X days to
  // close" verdict — the clock's days-to-close belongs to the CURRENT FY and is
  // nonsensical against last year's numbers. Report the final position instead.
  const verdictTone: VerdictTone = fyc.isPriorYear
    ? "amber"
    : remainingBalance < 100_000
      ? "red"
      : remainingBalance < 300_000
        ? "amber"
        : "green";
  const verdictPhrase =
    verdictTone === "red"
      ? "tight"
      : verdictTone === "amber"
        ? "manageable but watched"
        : "in good shape";
  const daysPhrase =
    daysToClose >= 0
      ? `${daysToClose} day${daysToClose === 1 ? "" : "s"} to fiscal close (June 30)`
      : "the fiscal year is closed";
  const verdictText = fyc.isPriorYear
    ? `${fyLabel} levy is closed — final position: ${usd0(remainingBalance)} available. ${fyc.currentFy} levy data (fund ${fyc.currentFund}) is not yet loaded from the county.`
    : `${fyLabel} levy is ${verdictPhrase} — ${usd0(remainingBalance)} available with ${daysPhrase}. Finance is closing open POs to free up more.`;

  return (
    <PageShell>
      <div>
        <PageHeader
          eyebrow="FINANCE"
          title={`Levy Funds — ${fyLabel} Position${
            fyc.isPriorYear ? " (prior year)" : ""
          }`}
          subtitle={
            <>
              {fyLabel} · fund {fyc.fund} · PWC appropriation (Oracle/Mobius) ·{" "}
              {summary?.as_of_date ? `as of ${summary.as_of_date}` : "loading…"}
              {summary?.funds_available_note
                ? ` · ${summary.funds_available_note}`
                : ""}
            </>
          }
          headerRight={<FundBadge kind="levy" />}
        />
        {verdictReady && (
          <PageIntro tone={verdictTone}>{verdictText}</PageIntro>
        )}
        <FreshnessStrip className="mt-3" />
        <StaleDataBanner className="mt-3" />
        <PriorYearBanner ctx={fyc} asOf={fyAsOf} className="mt-3" />
      </div>

      <FetchErrorBanner
        error={kpisError ?? summaryError}
        what="the levy position"
      />

      {/* The detailed budgetary-control truth lives here — this page LEADS with
          the three-lens reconciliation (the real differentiator vs Overview,
          which owns the at-a-glance 4-KPI headline). This directly answers the
          finance-staff confusion: gross object-report "available" ($158,751)
          walked down to the controlling Funds Available ($24,913). */}
      <FundsAvailableReconciler
        data={availPair}
        isLoading={availPairLoading}
        priorYearFy={
          // The recon slice can lag the page FY (e.g. FY27 page, FY26 recon rows
          // until the first FY27 recon capture) — label it by ITS OWN FY when
          // the resolver fell back, so the $61K close never reads as today's.
          availPair?.is_fallback && availPair.fiscal_year
            ? availPair.fiscal_year
            : fyc.isPriorYear
              ? fyLabel
              : null
        }
      />

      {/* Budget-control detail — collapsed by default so the headline reads
          clean for non-finance board members. Expand for the object-level lens
          (discretionary available, % consumed, objects over budget). */}
      <div>
        <button
          type="button"
          onClick={() => setShowBudgetDetail((v) => !v)}
          aria-expanded={showBudgetDetail}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-theme-secondary hover:text-theme-primary transition-colors"
        >
          <span className="text-theme-muted">
            {showBudgetDetail ? "▾" : "▸"}
          </span>
          {showBudgetDetail ? "Hide" : "Show"} budget-control detail
        </button>

        {showBudgetDetail && (
          <div className="mt-4 space-y-6">
            {/* Secondary signal row — budget-control lens (object-level) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <KPICard
                label={
                  <span className="inline-flex items-center gap-1">
                    Discretionary Available <InfoTip term="discretionary" />
                  </span>
                }
                value={usd0(available)}
                subtitle="Board snapshot — revenue excluded"
                accent={availableAccent(available)}
                isLoading={sumLoading}
              />
              <KPICard
                label={
                  <span className="inline-flex items-center gap-1">
                    Open Encumbrance <InfoTip term="encumbrance" />
                  </span>
                }
                value={usdC(encumbered)}
                subtitle={`${poCount} open POs · ${cfCount} carryover`}
                accent="amber"
                isLoading={sumLoading}
              />
              <KPICard
                label="Budget Consumed"
                value={`${consumedPct}%`}
                subtitle={`${usdC(expConsumed)} of ${usdC(expBudget)}`}
                accent={
                  consumedPct >= 95
                    ? "red"
                    : consumedPct >= 80
                      ? "amber"
                      : "green"
                }
                isLoading={sumLoading}
              />
              <KPICard
                label={
                  <span className="inline-flex items-center gap-1">
                    Objects Over Budget <InfoTip term="object" />
                  </span>
                }
                value={overCount}
                subtitle="Projected ≥ 100% of budget"
                accent={overCount > 0 ? "red" : "green"}
                isLoading={watchLoading}
              />
            </div>

            {/* Insight callouts — budget control signal */}
            {insights.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {insights.map((r) => (
                  <div
                    key={r.object_segment}
                    className="glass rounded-xl px-4 py-3 border-l-4 border-status-red"
                  >
                    <div
                      className="text-xs font-semibold text-theme-primary truncate"
                      title={r.object_desc}
                    >
                      {shortObj(String(r.object_desc))}
                    </div>
                    <div className="text-lg font-bold text-status-red mt-0.5">
                      {r.pct_projected > 900 ? "—" : `${r.pct_projected}%`}
                    </div>
                    <div className="text-xs text-theme-muted mt-0.5">
                      {usdC(r.actual)} spent + {usdC(r.open_encumbrance)}{" "}
                      encumbered vs {usdC(r.budget)} budget
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Budget Control — projected landing by object (the hero) */}
      <ChartCard
        title="Budget Control by Object — Are We Tracking?"
        subtitle="Budget vs spend + open encumbrance (projected landing). Watch where the stacked bar passes the budget bar."
        empty={!watchLoading && chartRows.length === 0}
        minHeight={120}
        headerRight={
          <div className="flex items-center gap-1">
            {(["watch", "all"] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setWatchScope(k)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${watchScope === k ? PILL_ACTIVE : "bg-theme-overlay text-theme-muted hover:text-theme-primary"}`}
              >
                {k === "watch" ? "Watchlist" : "All objects"}
              </button>
            ))}
          </div>
        }
      >
        {chartRows.length > 0 && (
          <ResponsiveContainer
            width="100%"
            height={Math.max(260, chartRows.length * 34)}
          >
            <BarChart
              data={chartRows}
              layout="vertical"
              margin={{ top: 4, right: 64, bottom: 4, left: 8 }}
              barGap={2}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={GRID}
                horizontal={false}
              />
              <XAxis
                type="number"
                tickFormatter={(v) => usdC(Number(v))}
                tick={{ fontSize: 11, fill: MUTED }}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={160}
                tick={{ fontSize: 10, fill: MUTED }}
              />
              <Tooltip
                formatter={(v, k) => [usd0(Number(v)), k as string]}
                {...TOOLTIP_PROPS}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar
                dataKey="Budget"
                stackId="b"
                fill={MUTED}
                fillOpacity={0.45}
                radius={[0, 3, 3, 0]}
                maxBarSize={11}
              />
              <Bar dataKey="Spend" stackId="p" fill={ACCENT} maxBarSize={11} />
              <Bar
                dataKey="Encumbrance"
                stackId="p"
                fill={COLORS[3]}
                radius={[0, 3, 3, 0]}
                maxBarSize={11}
              >
                <LabelList
                  dataKey="pct"
                  position="right"
                  formatter={(v) => {
                    const n = Number(v);
                    return !Number.isFinite(n) || n > 900 ? "" : `${n}%`;
                  }}
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    fill: "var(--text-muted)",
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
        <p className="text-xs text-theme-muted mt-2">
          Top (faint) bar = budget. Bottom bar = spend (teal) + open encumbrance
          (amber); the % is projected spend as a share of budget. Where amber
          pushes the bottom bar past the budget bar, that object is on track to
          overrun.
        </p>
      </ChartCard>

      {/* Funds-available trend */}
      <ChartCard
        title="Funds-Available Trend"
        subtitle={`Discretionary operating cash by snapshot date (${fyLabel})`}
        empty={!trendLoading && trendData.length === 0}
      >
        {trendData.length > 0 ? (
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart
              data={trendData}
              margin={{ top: 8, right: 16, bottom: 4, left: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: MUTED }} />
              <YAxis
                tickFormatter={(v) => usdC(Number(v))}
                tick={{ fontSize: 12, fill: MUTED }}
                width={56}
              />
              <Tooltip
                formatter={(v) => [usd0(Number(v)), "Available"]}
                {...TOOLTIP_PROPS}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={ACCENT}
                strokeWidth={2.5}
                dot={{ r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-theme-muted">No snapshots yet.</p>
        )}
      </ChartCard>

      {/* Budget vs consumed by object */}
      <ChartCard
        title="Budget vs Consumed — by Object"
        subtitle="Expenditure objects, ordered by funds available (most-consumed first)"
        empty={!objLoading && objRows.length === 0}
        minHeight={120}
        headerRight={
          <ExportCsvButton
            filename="dcvfd-levy-by-object"
            rows={objRows}
            columns={objExportColumns}
          />
        }
      >
        <div className="overflow-x-auto -mx-4 md:-mx-5">
          <table className="w-full min-w-[760px] text-left">
            <caption className="sr-only">
              Budget versus consumed by object
            </caption>
            <thead>
              <tr className="border-b border-theme-subtle">
                <th scope="col" className={TH}>
                  <span className="inline-flex items-center gap-1">
                    Object <InfoTip term="object" />
                  </span>
                </th>
                <th scope="col" className={TH}>
                  Description
                </th>
                <th scope="col" className={THR}>
                  Budget
                </th>
                <th scope="col" className={THR}>
                  <span className="inline-flex items-center gap-1">
                    Committed <InfoTip term="commitment" />
                  </span>
                </th>
                <th scope="col" className={THR}>
                  <span className="inline-flex items-center gap-1">
                    Obligated <InfoTip term="obligation" />
                  </span>
                </th>
                <th scope="col" className={THR}>
                  Actual
                </th>
                <th scope="col" className={THR}>
                  <span className="inline-flex items-center gap-1">
                    Budget Remaining <InfoTip term="funds_available" />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {objLoading
                ? Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="border-b border-theme-subtle/50">
                      {Array.from({ length: 7 }).map((__, j) => (
                        <td key={j} className="px-4 py-3">
                          <div
                            className="skeleton h-4 rounded-md"
                            style={{
                              width: `${50 + ((i * 7 + j * 11) % 60)}px`,
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                : objRows.map((r, idx) => {
                    const avail = asNum(r.funds_available);
                    return (
                      <tr
                        key={`${r.object_segment}-${idx}`}
                        className="hover:bg-theme-overlay/50 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm font-mono text-theme-primary">
                          {r.object_segment}
                        </td>
                        <td
                          className="px-4 py-3 text-sm text-theme-secondary max-w-[260px] truncate"
                          title={r.object_desc}
                        >
                          {r.object_desc}
                        </td>
                        <td className="px-4 py-3 text-sm text-right tabular-nums text-theme-secondary">
                          {usd0(asNum(r.budget_amount))}
                        </td>
                        <td className="px-4 py-3 text-sm text-right tabular-nums text-theme-secondary">
                          {usd0(asNum(r.commitment_amount))}
                        </td>
                        <td className="px-4 py-3 text-sm text-right tabular-nums text-theme-secondary">
                          {usd0(asNum(r.obligation_amount))}
                        </td>
                        <td className="px-4 py-3 text-sm text-right tabular-nums text-theme-secondary">
                          {usd0(asNum(r.actual_amount))}
                        </td>
                        <td className="px-4 py-3 text-sm text-right tabular-nums font-semibold">
                          <span
                            className={
                              avail < 0
                                ? "text-status-red"
                                : "text-theme-primary"
                            }
                          >
                            {usd0(avail)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Open POs */}
      <ChartCard
        title={
          fyc.isPriorYear
            ? `Open Purchase Orders — ${fyLabel}, carrying into ${fyc.currentFy}`
            : "Open Purchase Orders"
        }
        subtitle={`${filteredPos.length} open encumbrances · ${usdC(filteredPos.reduce((s, r) => s + asNum(r.remaining), 0))} remaining · ${usdC(carryoverRem)} is carryover`}
        empty={!poLoading && filteredPos.length === 0}
        minHeight={120}
        headerRight={
          <div className="flex items-center gap-1">
            {(["all", "carryover"] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setPoScope(k)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${poScope === k ? PILL_ACTIVE : "bg-theme-overlay text-theme-muted hover:text-theme-primary"}`}
              >
                {k === "all" ? "All" : "Carryover only"}
              </button>
            ))}
          </div>
        }
      >
        <div className="overflow-x-auto -mx-4 md:-mx-5 max-h-[520px] overflow-y-auto">
          <table className="w-full min-w-[760px] text-left">
            <caption className="sr-only">Open purchase orders</caption>
            <thead className="sticky top-0 bg-theme-surface">
              <tr className="border-b border-theme-subtle">
                <th scope="col" className={TH}>
                  PO #
                </th>
                <th scope="col" className={TH}>
                  Supplier / Description
                </th>
                <th scope="col" className={TH}>
                  <span className="inline-flex items-center gap-1">
                    Object <InfoTip term="object" />
                  </span>
                </th>
                <th scope="col" className={`${TH} text-center`}>
                  <span className="inline-flex items-center gap-1">
                    Established <InfoTip term="carryover" />
                  </span>
                </th>
                <th scope="col" className={THR}>
                  Remaining
                </th>
              </tr>
            </thead>
            <tbody>
              {poLoading
                ? Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="border-b border-theme-subtle/50">
                      {Array.from({ length: 5 }).map((__, j) => (
                        <td key={j} className="px-4 py-3">
                          <div
                            className="skeleton h-4 rounded-md"
                            style={{
                              width: `${50 + ((i * 9 + j * 13) % 70)}px`,
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                : filteredPos.map((r, idx) => {
                    const isCarry = asNum(r.carryover) === 1;
                    const fy = r.fy_established
                      ? `FY${String(r.fy_established).slice(2)}`
                      : "—";
                    return (
                      <tr
                        key={`${r.po_number}-${idx}`}
                        className="hover:bg-theme-overlay/50 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm font-mono text-theme-primary whitespace-nowrap">
                          {r.po_number}
                        </td>
                        <td
                          className="px-4 py-3 text-sm text-theme-secondary max-w-[320px] truncate"
                          title={r.supplier || r.description || r.object_desc}
                        >
                          {r.supplier || r.description || r.object_desc || "—"}
                        </td>
                        <td
                          className="px-4 py-3 text-sm text-theme-secondary max-w-[200px] truncate"
                          title={r.object_desc}
                        >
                          <span className="font-mono">{r.object_segment}</span>{" "}
                          <span className="text-theme-muted">
                            {r.object_desc}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          {isCarry ? (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-status-amber/15 text-status-amber"
                              title="Carried over from a prior fiscal year"
                            >
                              ↺ {fy}
                            </span>
                          ) : (
                            <span className="text-xs text-theme-muted">
                              {fy}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-right tabular-nums font-semibold text-theme-primary">
                          {usd0(asNum(r.remaining))}
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
      </ChartCard>

      <p className="text-xs text-theme-muted">
        Cardinal rule: Funds Available = Budget − Commitments − Obligations −
        Expenditures (appropriation side only). Revenue is tracked separately
        and never added to available. Open POs show real open encumbrances
        (remaining &gt; 0); carryover (↺) marks POs established in a prior
        fiscal year. Source: Oracle Budgetary Control Analysis + Manage Orders
        export ({fyLabel}).
      </p>
    </PageShell>
  );
}
